import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import "../account.css";
import "./accountBillingPage.css";
import billingRequest from "../../../request/billing";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import BillingPageSkeleton from "./billingPageSkeleton/billingPageSkeleton";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import {
	AlertTriangle,
	CalendarX,
	Check,
	Copy,
	CreditCard,
	ExternalLink,
	Gift,
	Info,
	Link2,
	PiggyBank,
	Receipt,
	RotateCcw,
	Sparkles,
	Users,
	Wallet,
} from "lucide-react";

/* Paddle-ish status strings → the badge tone that reads right. */
const STATUS_TONE = {
	active: "ok",
	completed: "ok",
	paid: "ok",
	trialing: "info",
	reward_issued: "ok",
	paused: "warn",
	pending: "warn",
	pending_billing: "warn",
	pending_referrer_payment: "warn",
	past_due: "danger",
	unpaid: "danger",
	canceled: "danger",
	cancelled: "danger",
	incomplete: "danger",
	incomplete_expired: "danger",
};

const toneOf = (status) => STATUS_TONE[status] || "neutral";

const AccountBillingPage = () => {
	const { userData, language } = useContext(CombinedContext);
	const [billing, setBilling] = useState(null);
	const [loading, setLoading] = useState(true);
	const [referral, setReferral] = useState(null);
	const [refLoading, setRefLoading] = useState(true);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
	const [referralInput, setReferralInput] = useState("");
	const [referralUpdating, setReferralUpdating] = useState(false);
	const [copied, setCopied] = useState(null); // "code" | "link"
	const [shopBilling, setShopBilling] = useState(null);
	const [reactivating, setReactivating] = useState(false);

	const navigate = useNavigate();
	const shopId = userData.shop.shopId;
	const t = translations[language].billing;

	useEffect(() => {
		if (!shopId) {
			showBanner("error", t.errorLoading);
			setLoading(false);
			return;
		}
		loadBilling();
	}, [shopId]);

	useEffect(() => {
		if (userData?.user?.userId) loadReferral();
	}, [userData]);

	const loadReferral = async () => {
		try {
			const res = await billingRequest.getReferralDashboard(userData.user.userId, userData.cognitoTokens.AccessToken);
			setReferral(res);
		} catch (err) {
			console.error("Failed to load referral dashboard:", err);
			showBanner("error", t.referral.errorLoad);
		} finally {
			setRefLoading(false);
		}
	};

	const loadBilling = async () => {
		try {
			const res = await billingRequest.getBillingInfo(shopId, userData.cognitoTokens.AccessToken);
			const sub = res.billing?.subscription;
			let normalizedSub = null;

			if (sub) {
				const items = (sub.items || []).map((item) => {
					const unitPrice = item.amount ? Number(item.amount) / 100 : 0;
					const quantity = item.quantity ?? 1;
					return {
						priceId: item.priceId,
						productName: item.productName || "Suscripción Sin Fila",
						unitPrice,
						quantity,
						total: unitPrice * quantity,
						currency: item.currency,
						interval: item.interval,
					};
				});

				normalizedSub = {
					items,
					grandTotal: items.reduce((sum, i) => sum + i.total, 0).toFixed(2),
					frequency: sub.billingCycle?.frequency || 1,
					interval: sub.billingCycle?.interval || "month",
					status: sub.status,
					currency: sub.currency,
					nextBillingDate: sub.nextBillingDate,
					firstBillingDate: sub.firstBillingDate,
					startedAt: sub.startedAt,
					activeDiscount: sub.activeDiscount || null,
					scheduledChange: sub.scheduledChange || null,
					managementUrls: {
						update_payment_method: sub.managementUrls?.update_payment_method,
						cancel: sub.managementUrls?.cancel,
					},
				};
			}

			setShopBilling(res.shopBilling || null);
			setBilling({ ...res.billing, subscription: normalizedSub });
		} catch (err) {
			console.error("Failed to load billing info:", err);
			showBanner("error", t.errorLoading);
		} finally {
			setLoading(false);
		}
	};

	/* Suspended shops have no working self-serve path in Paddle's own UI — a
	   canceled subscription has no management URLs at all — so the server
	   decides how this shop gets back: fix the card, or buy a new subscription. */
	const handleReactivate = async () => {
		try {
			setReactivating(true);
			const res = await billingRequest.reactivate(shopId, userData.cognitoTokens.AccessToken);

			if (res.mode === "update_payment") {
				window.open(res.updatePaymentUrl, "_blank", "noopener,noreferrer");
				showBanner("success", t.suspension.updateOpened, 6000);
				return;
			}

			if (res.mode === "checkout") {
				navigate("/checkout", {
					state: { billingData: { transactionId: res.transactionId }, reactivate: true },
				});
				return;
			}

			if (res.mode === "resumed") {
				// Nothing was charged and nothing lapsed — the queued cancellation
				// was simply called off, so say that rather than "reactivated".
				showBanner("success", t.scheduledCancel.resumed, 6000);
				loadBilling();
				return;
			}

			// "recovered" / "not_suspended" — the shop is already square with billing.
			showBanner("success", t.suspension.reactivated);
			loadBilling();
		} catch (err) {
			console.error("Reactivation failed:", err);
			showBanner("error", t.suspension.error);
		} finally {
			setReactivating(false);
		}
	};

	const generateCode = async () => {
		try {
			await billingRequest.generateReferralCode({ userId: userData.user.userId, accessToken: userData.cognitoTokens.AccessToken });
			loadReferral();
		} catch (err) {
			showBanner("error", t.referral.errorGenerate);
		}
	};

	const updateReferral = async () => {
		try {
			setReferralUpdating(true);
			await billingRequest.updateReferral({ code: referralInput || null, accessToken: userData.cognitoTokens.AccessToken });
			showBanner("success", t.referral.applied);
			loadReferral();
		} catch (err) {
			showBanner("error", err.message || t.referral.errorApply);
		} finally {
			setReferralUpdating(false);
		}
	};

	const copy = async (text, type, message) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(type);
			setTimeout(() => setCopied((prev) => (prev === type ? null : prev)), 2000);
			showBanner("success", message);
		} catch (err) {
			console.error("Clipboard copy failed:", err);
		}
	};

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);
		setTimeout(() => setExiting(true), duration - 250);
		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	if (loading) return <BillingPageSkeleton />;

	const subscription = billing?.subscription || null;
	const upcomingPayment = billing?.upcomingPayment || null;
	const seats = billing?.seats || null;
	const billingReferral = billing?.referral || null;
	const transactions = billing?.transactions || [];
	const paymentMethod = billing?.customer?.paymentMethod || null;
	const referralLink = referral?.referralCode ? `https://reservasinfila.com/listyourshop/${referral.referralCode}` : null;
	const money = (currency, amount) => `${currency || ""} ${amount}`.trim();

	const isSuspended = shopBilling?.billingStatus === "suspended";
	const isWarning = shopBilling?.billingStatus === "warning";

	/* Paddle holds a cancellation as a scheduled change and leaves the status on
	   "active" until the period actually ends — so the badge alone would tell a
	   cancelled owner everything is fine right up until their shop vanished. */
	const scheduledChange = subscription?.scheduledChange || null;
	const cancelScheduled = scheduledChange?.action === "cancel" && !isSuspended;
	const cancelEffectiveDate = scheduledChange?.effectiveAt
		? new Date(scheduledChange.effectiveAt).toLocaleDateString()
		: null;

	/* The shop-level flags come from our own webhook bookkeeping, which can lag
	   or miss entirely. Paddle's own verdict on the subscription is the second
	   opinion: if it says dead or unpaid, the owner needs the recovery CTA even
	   though nothing ever flagged the shop. */
	const subIsDead = ["canceled", "cancelled"].includes(subscription?.status);
	const subIsUnpaid = ["past_due", "unpaid", "paused"].includes(subscription?.status);
	const needsRecovery = isSuspended || isWarning || subIsDead || subIsUnpaid;
	// Treat a dead subscription as suspended for wording, even if the flag never landed.
	const showAsSuspended = isSuspended || subIsDead;

	// Days left before the grace period ends and the shop gets delisted.
	const graceDaysLeft = shopBilling?.graceEndsAt
		? Math.max(0, Math.ceil((new Date(shopBilling.graceEndsAt) - Date.now()) / 86400000))
		: null;

	return (
		<div className="ac-page apb">
			<header className="ac-head">
				{/* <span className="ac-eyebrow">{translations[language].account?.menu?.billing || "Billing"}</span> */}
				<h1 className="ac-title">{t.title}</h1>
			</header>

			{/* ── SUSPENDED / PAST-DUE RECOVERY ──
			    The one place a suspended owner can get their business back online.
			    Sits above the grid so it's the first thing on the page. */}
			{needsRecovery && (
				<section className={`ac-card apb-alert ${showAsSuspended ? "is-danger" : "is-warn"}`}>
					<div className="ac-card-head">
						<span className={`ac-icon ${showAsSuspended ? "is-danger" : "is-warn"}`}>
							<AlertTriangle size={16} />
						</span>
						<h3>{showAsSuspended ? t.suspension.suspendedTitle : t.suspension.warningTitle}</h3>
					</div>

					<div className="ac-card-body">
						<p className="apb-alert-copy">
							{showAsSuspended
								? t.suspension.suspendedBody
								: t.suspension.warningBody.replace("{days}", graceDaysLeft ?? shopBilling?.graceDays ?? 7)}
						</p>

						<button className="ac-btn apb-alert-cta" onClick={handleReactivate} disabled={reactivating}>
							<RotateCcw size={14} />
							{reactivating
								? t.suspension.working
								: showAsSuspended
									? t.suspension.reactivateButton
									: t.suspension.fixPaymentButton}
						</button>
					</div>
				</section>
			)}

			{/* ── CANCELLATION SCHEDULED ──
			    Still live, still paid up, but Paddle has an end date queued. The
			    owner gets told when service stops and can call it off in one click
			    — Paddle's own portal offers no undo once cancel is scheduled. */}
			{cancelScheduled && (
				<section className="ac-card apb-alert is-warn">
					<div className="ac-card-head">
						<span className="ac-icon is-warn">
							<CalendarX size={16} />
						</span>
						<h3>{t.scheduledCancel.title}</h3>
					</div>

					<div className="ac-card-body">
						<p className="apb-alert-copy">
							{cancelEffectiveDate
								? t.scheduledCancel.body.replace("{date}", cancelEffectiveDate)
								: t.scheduledCancel.bodyNoDate}
						</p>

						<button className="ac-btn apb-alert-cta" onClick={handleReactivate} disabled={reactivating}>
							<RotateCcw size={14} />
							{reactivating ? t.suspension.working : t.scheduledCancel.keepButton}
						</button>
					</div>
				</section>
			)}

			<div className="apb-grid">
				{/* ══ MAIN COLUMN ══ */}
				<div className="apb-col">
					{/* ── CURRENT SUBSCRIPTION ── */}
					{subscription ? (
						<section className="ac-card">
							<div className="ac-card-head">
								<span className="ac-icon">
									<CreditCard size={16} />
								</span>
								<h3>{t.currentSubscription}</h3>
								<span className={`ac-badge ac-badge--${cancelScheduled ? "warn" : toneOf(subscription.status)}`}>
									<span className="ac-badge-dot" />
									{cancelScheduled
										? t.statusBadge.cancelScheduled
										: t.statusBadge[subscription.status] || subscription.status}
								</span>
							</div>

							<div className="ac-card-body">
								<div className="apb-amount">
									<span className="apb-amount-currency">{subscription.currency}</span>
									<span className="apb-amount-value">{subscription.grandTotal}</span>
									<span className="apb-amount-interval">/{subscription.interval}</span>
								</div>

								{/* A cancelled subscription has no next billing — showing one
								    was the clearest way the page misreported the state. */}
								{cancelScheduled ? (
									cancelEffectiveDate && <p className="apb-next is-warn">{t.serviceEndsOn.replace("{date}", cancelEffectiveDate)}</p>
								) : (
									subscription.nextBillingDate && (
										<p className="apb-next">
											{t.nextBillingOn.replace("{date}", new Date(subscription.nextBillingDate).toLocaleDateString())}
										</p>
									)
								)}

								<div className="apb-items">
									{subscription.items.map((item, idx) => (
										<div key={idx} className="apb-item">
											<div className="apb-item-copy">
												<span className="apb-item-name">{item.productName}</span>
												<span className="apb-item-unit">
													{item.quantity} × {money(subscription.currency, item.unitPrice.toFixed(2))}
												</span>
											</div>
											<span className="apb-item-total">
												{money(subscription.currency, item.total.toFixed(2))}
												<em>/{item.interval}</em>
											</span>
										</div>
									))}
								</div>

								{/* Past the cap the staff quantity above stops tracking the real
								    headcount, and a total that simply didn't move when someone was
								    hired reads as a bug. Only shown once a shop is actually over it —
								    below the cap there is nothing to explain. */}
								{seats?.capped && (
									<div className="ac-note is-ok">
										<Users size={14} />
										<span>
											{t.seatCap.note
												.replace("{staff}", seats.staff)
												.replace("{billed}", seats.billedStaff)
												.replace("{max}", seats.maxBilledStaff)}
										</span>
									</div>
								)}

								{subscription.activeDiscount && (
									<div className="ac-note is-ok">
										<Sparkles size={14} />
										<span>
											<strong>{t.discount.active}</strong>
											<br />
											{t.discount.appliedRange
												.replace("{from}", new Date(subscription.activeDiscount.startsAt).toLocaleDateString())
												.replace("{to}", new Date(subscription.activeDiscount.endsAt).toLocaleDateString())}
										</span>
									</div>
								)}

								<div className="ac-kv is-total">
									<span className="ac-kv-key">Total</span>
									<span className="ac-kv-val">
										{money(subscription.currency, subscription.grandTotal)}/{subscription.interval}
									</span>
								</div>

								{/* Paddle drops management_urls once a subscription is canceled.
								    Rendering the buttons anyway gives the owner two controls that
								    open about:blank — the dead end that made reactivation look
								    impossible. Show each only when Paddle actually gave us a link;
								    the reactivate banner above is the route back from here. */}
								{(subscription.managementUrls?.update_payment_method || (subscription.managementUrls?.cancel && !cancelScheduled)) && (
									<div className="apb-actions">
										{subscription.managementUrls?.update_payment_method && (
											<button
												className="ac-btn ac-btn--ghost"
												onClick={() => window.open(subscription.managementUrls.update_payment_method, "_blank", "noopener,noreferrer")}
											>
												<ExternalLink size={14} />
												{t.buttons.updatePaymentMethod}
											</button>
										)}
										{/* Already cancelled — offering "Cancel" again just confuses.
										    The banner above carries the only useful action now. */}
										{subscription.managementUrls?.cancel && !cancelScheduled && (
											<button
												className="ac-btn ac-btn--danger-ghost"
												onClick={() => window.open(subscription.managementUrls.cancel, "_blank", "noopener,noreferrer")}
											>
												{t.buttons.cancelSubscription}
											</button>
										)}
									</div>
								)}
							</div>
						</section>
					) : (
						<section className="ac-card">
							<div className="ac-card-head">
								<span className="ac-icon">
									<CreditCard size={16} />
								</span>
								<h3>{t.currentSubscription}</h3>
							</div>

							<div className="ac-card-body">
								<div className="ac-field">
									<p className="ac-label">{t.referral.applyTitle}</p>
									<div className="ac-inline">
										<input
											type="text"
											className="ac-input"
											placeholder={t.referral.applyPlaceholder}
											value={referralInput}
											onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
										/>
										<button className="ac-btn" onClick={updateReferral} disabled={referralUpdating}>
											{referralUpdating ? t.referral.applyLoading : t.referral.applyButton}
										</button>
									</div>
								</div>

								{userData?.user?.tempReferralCode && (
									<p className="apb-using">
										{t.referral.usingCode} <span className="ac-code">{userData.user.tempReferralCode}</span>
									</p>
								)}
							</div>
						</section>
					)}

					{/* ── UPCOMING PAYMENT ──
					    Suppressed once a cancellation is queued: there is no next
					    charge, so a total due here would contradict the banner. */}
					{upcomingPayment && !cancelScheduled && (
						<section className="ac-card">
							<div className="ac-card-head">
								<span className="ac-icon">
									<Wallet size={16} />
								</span>
								<h3>
									{t.upcomingPayment.title}
									<span className="ac-card-head-sub">{new Date(upcomingPayment.date).toLocaleDateString()}</span>
								</h3>
							</div>

							<div className="ac-card-body">
								<div className="ac-kv">
									<span className="ac-kv-key">{t.upcomingPayment.subtotal}</span>
									<span className="ac-kv-val">
										{money(upcomingPayment.currency, (upcomingPayment.subtotalCents / 100).toFixed(2))}
									</span>
								</div>

								{upcomingPayment.discountCents > 0 && (
									<div className="ac-kv is-credit">
										<span className="ac-kv-key">{t.upcomingPayment.discount}</span>
										<span className="ac-kv-val">
											− {money(upcomingPayment.currency, (upcomingPayment.discountCents / 100).toFixed(2))}
										</span>
									</div>
								)}

								<div className="ac-kv is-total">
									<span className="ac-kv-key">{t.upcomingPayment.totalDue}</span>
									<span className="ac-kv-val">
										{money(upcomingPayment.currency, (upcomingPayment.totalCents / 100).toFixed(2))}
									</span>
								</div>
							</div>
						</section>
					)}

					{/* ── TRANSACTION HISTORY ── */}
					{transactions.length > 0 && (
						<section className="ac-card">
							<div className="ac-card-head">
								<span className="ac-icon">
									<Receipt size={16} />
								</span>
								<h3>{t.transactions.title}</h3>
								<span className="ac-badge ac-badge--neutral">{transactions.length}</span>
							</div>

							<div className="ac-card-body is-flush">
								<div className="ac-table-wrap apb-txn-table">
									<table className="ac-table">
										<thead>
											<tr>
												<th>{t.transactions.date}</th>
												<th>{t.transactions.invoice}</th>
												<th className="is-num">{t.transactions.subtotal}</th>
												<th className="is-num">{t.transactions.discount}</th>
												<th className="is-num">{t.transactions.total}</th>
												<th>{t.transactions.status}</th>
											</tr>
										</thead>
										<tbody>
											{transactions.map((txn, i) => (
												<tr key={i}>
													<td>{txn.billedAt ? new Date(txn.billedAt).toLocaleDateString() : t.transactions.noInvoice}</td>
													<td className="is-muted">{txn.invoiceNumber ? `#${txn.invoiceNumber}` : t.transactions.noInvoice}</td>
													<td className="is-num">{money(txn.currency, (parseInt(txn.subtotal || "0") / 100).toFixed(2))}</td>
													<td className={`is-num ${parseInt(txn.discount || "0") > 0 ? "apb-txn-discount" : "is-muted"}`}>
														{parseInt(txn.discount || "0") > 0
															? `− ${money(txn.currency, (parseInt(txn.discount) / 100).toFixed(2))}`
															: t.transactions.noInvoice}
													</td>
													<td className="is-num">{money(txn.currency, (parseInt(txn.total || "0") / 100).toFixed(2))}</td>
													<td>
														<span className={`ac-badge ac-badge--${toneOf(txn.status)}`}>
															{t.statusBadge[txn.status] || txn.status}
														</span>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								{/* Narrow screens get cards — a 6-column table can't shrink honestly */}
								<div className="apb-txn-cards">
									{transactions.map((txn, i) => (
										<div key={i} className="apb-txn">
											<div className="apb-txn-copy">
												<span className="apb-txn-date">
													{txn.billedAt ? new Date(txn.billedAt).toLocaleDateString() : t.transactions.noInvoice}
												</span>
												<span className="apb-txn-ref">
													{txn.invoiceNumber ? `#${txn.invoiceNumber}` : t.transactions.noInvoice}
												</span>
												{parseInt(txn.discount || "0") > 0 && (
													<span className="apb-txn-discount">
														−{money(txn.currency, (parseInt(txn.discount) / 100).toFixed(2))}{" "}
														{t.transactions.discount.toLowerCase()}
													</span>
												)}
											</div>

											<div className="apb-txn-right">
												<span className="apb-txn-amount">
													{money(txn.currency, (parseInt(txn.total || "0") / 100).toFixed(2))}
												</span>
												<span className={`ac-badge ac-badge--${toneOf(txn.status)}`}>
													{t.statusBadge[txn.status] || txn.status}
												</span>
											</div>
										</div>
									))}
								</div>
							</div>
						</section>
					)}
				</div>

				{/* ══ SIDE COLUMN ══ */}
				<div className="apb-col apb-col--side">
					{/* ── PAYMENT METHOD ── */}
					<section className="ac-card">
						<div className="ac-card-head">
							<span className="ac-icon">
								<CreditCard size={16} />
							</span>
							<h3>{t.paymentMethod.title}</h3>
						</div>

						<div className="ac-card-body">
							{paymentMethod ? (
								<>
									<div className="apb-pm">
										<span className="apb-pm-brand">{paymentMethod.brand?.toUpperCase()}</span>
										<span className="apb-pm-digits">•••• {paymentMethod.last4}</span>
									</div>

									<div className="ac-kv">
										<span className="ac-kv-key">{t.paymentMethod.expires}</span>
										<span className="ac-kv-val">
											{paymentMethod.expMonth}/{paymentMethod.expYear}
										</span>
									</div>
								</>
							) : (
								<p className="apb-muted">{t.paymentMethod.noStored}</p>
							)}
						</div>
					</section>

					{/* ── REFERRAL CREDITS ── */}
					{billingReferral && (
						<section className="ac-card">
							<div className="ac-card-head">
								<span className="ac-icon is-gold">
									<PiggyBank size={16} />
								</span>
								<h3>{t.referralCredits.title}</h3>
							</div>

							<div className="ac-card-body">
								<div className="apb-credit">
									<span className="apb-credit-amount">${billingReferral.creditBalance.toFixed(2)}</span>
									<span className="apb-credit-label">{t.referralCredits.availableCredit}</span>
								</div>

								<div className="ac-kv">
									<span className="ac-kv-key">{t.referralCredits.rewardsEarned}</span>
									<span className="ac-kv-val">
										{billingReferral.totalRewardCount}{" "}
										{billingReferral.totalRewardCount !== 1 ? t.referralCredits.referrals : t.referralCredits.referral}
									</span>
								</div>

								{billingReferral.usedReferralCode && (
									<>
										<hr className="ac-rule" />
										<p className="ac-label">{t.referralCredits.usedCode}</p>
										<div className="apb-used">
											<span className="ac-code">{billingReferral.usedReferralCode.code}</span>
											<span
												className={`ac-badge ac-badge--${
													billingReferral.usedReferralCode.rewardIssued
														? "ok"
														: toneOf(billingReferral.usedReferralCode.status)
												}`}
											>
												{billingReferral.usedReferralCode.rewardIssued
													? t.referralCredits.rewarded
													: t.referralCredits.statusLabels[billingReferral.usedReferralCode.status] ||
														billingReferral.usedReferralCode.status}
											</span>
										</div>
									</>
								)}

								{billingReferral.referralsIssued?.length > 0 && (
									<>
										<hr className="ac-rule" />
										<p className="ac-label">{t.referralCredits.shopsReferred}</p>
										<div className="apb-list">
											{billingReferral.referralsIssued.map((r, i) => (
												<div key={i} className="apb-list-row">
													<span className="apb-list-name">{r.refereeEmail || r.refereeShopId}</span>
													<span className={`ac-badge ac-badge--${r.rewardIssued ? "ok" : toneOf(r.status)}`}>
														{r.rewardIssued
															? t.referralCredits.rewarded
															: t.referralCredits.statusLabels[r.status] || r.status}
													</span>
												</div>
											))}
										</div>
									</>
								)}
							</div>
						</section>
					)}

					{/* ── REFERRAL PROGRAM ── */}
					<section className="ac-card">
						<div className="ac-card-head">
							<span className="ac-icon is-gold">
								<Gift size={16} />
							</span>
							<h3>{t.referral.title}</h3>
						</div>

						<div className="ac-card-body">
							{refLoading ? (
								<p className="apb-muted">{t.referral.loading}</p>
							) : referral?.referralCode ? (
								<>
									<div className="ac-field">
										<p className="ac-label">{t.referral.codeLabel}</p>
										<div className="ac-copy">
											<span className="ac-copy-text">{referral.referralCode}</span>
											<button
												className={`ac-copy-btn ${copied === "code" ? "is-copied" : ""}`}
												onClick={() => copy(referral.referralCode, "code", t.referral.copied)}
											>
												{copied === "code" ? <Check size={14} /> : <Copy size={14} />}
												{t.referral.copyCode}
											</button>
										</div>
									</div>

									<div className="ac-field">
										<p className="ac-label">{t.referral.linkLabel}</p>
										<div className="ac-copy">
											<span className="ac-copy-text is-link">{referralLink}</span>
											<button
												className={`ac-copy-btn ${copied === "link" ? "is-copied" : ""}`}
												onClick={() => copy(referralLink, "link", t.referral.linkCopied)}
											>
												{copied === "link" ? <Check size={14} /> : <Link2 size={14} />}
												{t.referral.copyLink}
											</button>
										</div>
									</div>
								</>
							) : (
								<div className="ac-empty">
									<span className="ac-icon is-gold">
										<Gift size={18} />
									</span>
									<p>{t.referral.noCode}</p>
									<button className="ac-btn ac-btn--sm" onClick={generateCode}>
										{t.referral.generate}
									</button>
								</div>
							)}
						</div>
					</section>

					<div className="ac-note">
						<Info size={14} />
						<span>{t.manageSubscription}</span>
					</div>
				</div>
			</div>

			{banner && <BannerMessage type={banner.type} text={banner.text} exiting={exiting} />}
		</div>
	);
};

export default AccountBillingPage;
